/**
 * Google Analytics 4 (GA4) Event Tracking Utility
 * Measurement ID: G-415282216
 */

// Check if gtag is available
const isGtagAvailable = () => typeof window.gtag === 'function';

/**
 * Track a custom event in GA4
 * @param {string} eventName - Name of the event
 * @param {object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  const payload = {
    ...params,
    timestamp: new Date().toISOString(),
  };

  // Always push to dataLayer first — GTM is the source of truth and may be
  // ready before gtag finishes bootstrapping. This avoids dropped funnel
  // events fired in useEffect during initial mount (gtag race condition).
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });
  }

  if (!isGtagAvailable()) {
    // Already captured via dataLayer above; no further action needed.
    return;
  }

  window.gtag('event', eventName, payload);
};

/**
 * Track page view (for SPA navigation)
 * @param {string} pagePath - Page path
 * @param {string} pageTitle - Page title
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('config', 'G-415282216', {
    page_path: pagePath,
    page_title: pageTitle
  });
};

// ============================================
// GOOGLE ADS CONVERSION EVENTS (AW-16606493089)
// ============================================
// IMPORTANT: To bind conversion events to the ad account, set these labels
// from Google Ads → Tools → Conversions → New conversion → "Website".
// For each event, Google gives you a label like "abcDEF123_xyz". Put that
// label in the env var below (separated by `/` from the account ID).
//
// Until labels are set, events still fire to GA4 (for funnel analysis) but
// won't show in Google Ads as conversions.
const AW_ID = 'AW-16606493089';
const AW_LABELS = {
  lead_form_submit:        process.env.REACT_APP_AW_LABEL_LEAD || '',
  whatsapp_click:          process.env.REACT_APP_AW_LABEL_WHATSAPP || '',
  phone_click:             process.env.REACT_APP_AW_LABEL_PHONE || '',
  demo_booking:            process.env.REACT_APP_AW_LABEL_DEMO || '',
  pricing_compare:         process.env.REACT_APP_AW_LABEL_PRICING || '',
  proposal_download:       process.env.REACT_APP_AW_LABEL_PROPOSAL || '',
};

/**
 * Fire a Google Ads conversion event. Safely no-ops if gtag or label missing.
 * @param {string} kind  One of the keys in AW_LABELS
 * @param {object} params  Optional: value, currency, transaction_id, custom labels
 */
export const trackGoogleAdsConversion = (kind, params = {}) => {
  if (!isGtagAvailable()) return;
  const label = AW_LABELS[kind];
  if (!label) {
    // No label configured for this conversion type yet — GA4 still fires
    // (handled by callers), but skip the Google Ads side.
    return;
  }
  window.gtag('event', 'conversion', {
    send_to: `${AW_ID}/${label}`,
    value: params.value || 1,
    currency: params.currency || 'INR',
    transaction_id: params.transaction_id || `${kind}-${Date.now()}`,
  });
};

// ============================================
// WHATSAPP UTM-ATTRIBUTED URL BUILDER
// ============================================
/**
 * Build a WhatsApp URL that prefixes the message with a UTM-attribution line.
 * Result is parseable in the WhatsApp conversation thread (so sales can map
 * the lead back to the originating page) while staying readable to the user.
 *
 * Example output message:
 *   "[ref: /nykaa-fashion-seller-registration]\n\nHi! I am interested..."
 *
 * @param {string} phone        Phone number (digits only, with country code)
 * @param {string} userMessage  The original prefilled message
 * @returns {string} fully-qualified https://wa.me/... URL
 */
export const buildWhatsAppUrl = (phone, userMessage) => {
  const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';
  const search = (typeof window !== 'undefined' && window.location && window.location.search) || '';
  const utmCampaign = path.replace(/^\//, '').replace(/\//g, '-') || 'home';
  // Compact ref line — sales can grep the WhatsApp thread for the slug.
  const ref = `[ref: ${path}${search ? ' · src=' + search.slice(1, 60) : ''}]`;
  const fullMessage = `${ref}\n\n${userMessage || 'Hi! I am interested in your services.'}`;
  // Also expose UTM via query — picked up by GA4 cross-domain attribution if user opens wa.me in mobile browser first
  const wa = new URL(`https://wa.me/${phone}`);
  wa.searchParams.set('text', fullMessage);
  wa.searchParams.set('utm_source', 'zarooriretail');
  wa.searchParams.set('utm_medium', 'whatsapp');
  wa.searchParams.set('utm_campaign', utmCampaign);
  return wa.toString();
};



/**
 * Track demo booking submission
 */
export const trackDemoBooking = (brandName, city) => {
  trackEvent('demo_booking', {
    event_category: 'conversion',
    event_label: brandName,
    brand_name: brandName,
    city: city,
    value: 1
  });
  
  // Also send as conversion
  trackEvent('conversion', {
    send_to: 'G-415282216',
    event_category: 'lead',
    event_label: 'demo_booking'
  });
};

/**
 * Track qualified lead submission (Meta landing page)
 */
export const trackQualifiedLead = (brandName, category, marketplace) => {
  trackEvent('qualified_lead', {
    event_category: 'conversion',
    event_label: brandName,
    brand_name: brandName,
    category: category,
    target_marketplace: marketplace,
    value: 1
  });
  
  trackEvent('conversion', {
    send_to: 'G-415282216',
    event_category: 'lead',
    event_label: 'qualified_lead'
  });
};

/**
 * Track newsletter subscription
 */
export const trackNewsletterSignup = (frequency) => {
  trackEvent('newsletter_signup', {
    event_category: 'engagement',
    event_label: frequency,
    frequency: frequency,
    value: 1
  });
};

/**
 * Track contact form submission
 */
export const trackContactSubmit = (category) => {
  trackEvent('contact_submit', {
    event_category: 'conversion',
    event_label: category,
    value: 1
  });
  trackGoogleAdsConversion('lead_form_submit', { value: 1 });
};

/**
 * Track Compare Pricing Widget interactions
 */
export const trackPricingCompareStart = (source) => {
  trackEvent('pricing_compare_start', {
    event_category: 'engagement',
    event_label: source || 'pricing-widget',
    source: source || 'page',
  });
};

export const trackPricingCompareSubmit = (marketplace, services, tier, monthlyRevenue) => {
  trackEvent('pricing_compare_submit', {
    event_category: 'conversion',
    event_label: marketplace,
    selected_services: (services || []).join(','),
    selected_tier: tier,
    monthly_revenue: monthlyRevenue || 'unspecified',
    value: 1,
  });
  trackGoogleAdsConversion('pricing_compare', { value: 1 });
};

export const trackProposalDownload = (proposalId) => {
  trackEvent('proposal_download', {
    event_category: 'conversion',
    event_label: proposalId || 'proposal',
    value: 1,
  });
  trackGoogleAdsConversion('proposal_download', { value: 1 });
};

// ============== FUNNEL EVENTS (post-quote actions) ==============
// Fire AFTER a user has submitted the pricing widget — lets you build the
// real conversion funnel: visit → start → submit → download → whatsapp/call.

export const trackWhatsAppAfterQuote = (proposalId) => {
  trackEvent('whatsapp_after_quote', {
    event_category: 'conversion',
    event_label: proposalId || 'no-id',
    value: 1,
  });
  trackGoogleAdsConversion('whatsapp_click', { value: 1 });
};

export const trackCallAfterQuote = (proposalId) => {
  trackEvent('call_after_quote', {
    event_category: 'conversion',
    event_label: proposalId || 'no-id',
    value: 1,
  });
  trackGoogleAdsConversion('phone_click', { value: 1 });
};

export const trackConsultationAfterQuote = (proposalId) => {
  trackEvent('consultation_after_quote', {
    event_category: 'conversion',
    event_label: proposalId || 'no-id',
    value: 1,
  });
  trackGoogleAdsConversion('demo_booking', { value: 1 });
};

/**
 * Track a consultation booking click (any "Book a call" / "Schedule consultation" CTA)
 */
export const trackConsultationBooking = (source) => {
  trackEvent('consultation_booking', {
    event_category: 'conversion',
    event_label: source || 'cta',
    value: 1,
  });
  trackGoogleAdsConversion('demo_booking', { value: 1 });
};

// ============== ENGAGEMENT EVENTS ==============

/**
 * Track blog read
 */
export const trackBlogRead = (slug, title, category) => {
  trackEvent('blog_read', {
    event_category: 'content',
    event_label: title,
    blog_slug: slug,
    blog_category: category
  });
};

/**
 * Track blog share
 */
export const trackBlogShare = (slug, platform) => {
  trackEvent('share', {
    event_category: 'engagement',
    event_label: slug,
    method: platform,
    content_type: 'blog'
  });
};

/**
 * Track CTA button click
 */
export const trackCTAClick = (ctaName, location) => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    event_label: ctaName,
    cta_name: ctaName,
    location: location
  });
};

/**
 * Track service page view
 */
export const trackServiceView = (serviceName) => {
  trackEvent('service_view', {
    event_category: 'content',
    event_label: serviceName,
    service_name: serviceName
  });
};

/**
 * Track chatbot interaction
 */
export const trackChatbotOpen = () => {
  trackEvent('chatbot_open', {
    event_category: 'engagement',
    event_label: 'ai_chatbot'
  });
};

export const trackChatbotMessage = () => {
  trackEvent('chatbot_message', {
    event_category: 'engagement',
    event_label: 'ai_chatbot'
  });
};

/**
 * Track WhatsApp click
 */
export const trackWhatsAppClick = (source) => {
  trackEvent('whatsapp_click', {
    event_category: 'engagement',
    event_label: source || 'whatsapp_widget',
    source: source || 'whatsapp_widget',
    page_path: (typeof window !== 'undefined' && window.location.pathname) || '',
  });
  trackGoogleAdsConversion('whatsapp_click', { value: 1 });
};

/**
 * Track phone call click
 */
export const trackPhoneClick = (phoneNumber) => {
  trackEvent('phone_click', {
    event_category: 'engagement',
    event_label: phoneNumber
  });
  trackGoogleAdsConversion('phone_click', { value: 1 });
};

/**
 * Track video play (YouTube testimonials)
 */
export const trackVideoPlay = (videoTitle) => {
  trackEvent('video_start', {
    event_category: 'engagement',
    event_label: videoTitle,
    video_title: videoTitle
  });
};

/**
 * Track scroll depth (for important pages)
 */
export const trackScrollDepth = (percentage, pagePath) => {
  trackEvent('scroll', {
    event_category: 'engagement',
    event_label: `${percentage}%`,
    percent_scrolled: percentage,
    page_path: pagePath
  });
};

// ============== E-COMMERCE STYLE EVENTS ==============

/**
 * Track product/service view (ABT/ABR plans)
 */
export const trackProductView = (productName, productCategory) => {
  trackEvent('view_item', {
    event_category: 'ecommerce',
    event_label: productName,
    items: [{
      item_name: productName,
      item_category: productCategory
    }]
  });
};

/**
 * Track "Get Started" or pricing inquiry
 */
export const trackBeginCheckout = (planName) => {
  trackEvent('begin_checkout', {
    event_category: 'ecommerce',
    event_label: planName,
    items: [{
      item_name: planName
    }]
  });
};

export default {
  trackEvent,
  trackPageView,
  trackGoogleAdsConversion,
  buildWhatsAppUrl,
  trackDemoBooking,
  trackQualifiedLead,
  trackNewsletterSignup,
  trackContactSubmit,
  trackPricingCompareStart,
  trackPricingCompareSubmit,
  trackProposalDownload,
  trackConsultationBooking,
  trackBlogRead,
  trackBlogShare,
  trackCTAClick,
  trackServiceView,
  trackChatbotOpen,
  trackChatbotMessage,
  trackWhatsAppClick,
  trackPhoneClick,
  trackVideoPlay,
  trackScrollDepth,
  trackProductView,
  trackBeginCheckout
};
